import type {
  BusinessExpenseReceiptStatus,
  BusinessExpenseVendorRow,
  ReportTransactionRow,
} from '../../types/report.types';

function receiptStatusForCharges(rows: ReportTransactionRow[]): BusinessExpenseReceiptStatus {
  if (!rows.length) return 'None on file';
  const withReceipt = rows.filter((r) => r.receiptAttached).length;
  if (withReceipt === 0) return 'None on file';
  if (withReceipt === rows.length) return 'All on file';
  return 'Partial';
}

function dominantBusinessPercent(rows: ReportTransactionRow[]): number | null {
  const values = rows.map((r) => r.businessUsePercent).filter((v): v is number => v != null);
  if (!values.length) return null;
  const counts = new Map<number, number>();
  for (const v of values) {
    counts.set(v, (counts.get(v) ?? 0) + 1);
  }
  let best: number | null = null;
  let bestCount = 0;
  for (const [value, count] of counts) {
    if (count > bestCount) {
      best = value;
      bestCount = count;
    }
  }
  return best;
}

export function buildBusinessExpenseVendorSummary(
  transactions: ReportTransactionRow[]
): BusinessExpenseVendorRow[] {
  const byVendor = new Map<string, ReportTransactionRow[]>();
  for (const tx of transactions) {
    const key = tx.vendorLabel ?? tx.description;
    const list = byVendor.get(key) ?? [];
    list.push(tx);
    byVendor.set(key, list);
  }

  return Array.from(byVendor.entries())
    .map(([vendorLabel, rows]) => {
      const months = new Set(rows.map((r) => r.date.slice(0, 7)));
      const totalAmountAud = rows.reduce((s, r) => s + (r.expenseAmountAud ?? Math.abs(r.amount)), 0);
      const claimableParts = rows
        .map((r) => r.claimableAmountAud)
        .filter((v): v is number => v != null);
      const claimableAmountAud = claimableParts.length
        ? claimableParts.reduce((a, b) => a + b, 0)
        : null;
      return {
        vendorLabel,
        chargeCount: rows.length,
        monthsActive: months.size,
        totalAmountAud,
        businessUsePercent: dominantBusinessPercent(rows),
        claimableAmountAud,
        receiptStatus: receiptStatusForCharges(rows),
      };
    })
    .sort((a, b) => b.totalAmountAud - a.totalAmountAud);
}
